<template>
  <div>
    <ts-page-title
      :title="$t('telegramMessage.historyPageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        { text: $t('telegramMessage.historyPageTitle'), active: true }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper>
        <div class="tw-flex tw-justify-between tw-items-center">
          <div class="tw-flex tw-items-center tw-space-x-3">
            <a-button type="primary" @click="$router.push({ name: 'telegram-message-create' })">
              {{ $t('telegramMessage.sendNew') }}
            </a-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width: 220px"
              @change="fetchData"
            />
          </div>
          <a-tooltip :title="$t('telegramMessage.search')">
            <a-input-search
              v-model="search"
              :placeholder="$t('telegramMessage.search')"
              style="width: 260px"
            />
          </a-tooltip>
        </div>
      </ts-panel-wrapper>
      <div class="tw-overflow-x-auto">
        <ts-table :columns="columns" :records="histories.data" :loading="loading">
          <template v-slot="{ record }">
            <td class="telegram-title-cell">{{ truncateText(record.title, 80) }}</td>
            <td class="telegram-content-cell">{{ truncateText(record.content, 120) }}</td>
            <td>{{ record.audience_type }}</td>
            <td>{{ record.recipients_count || 0 }}</td>
            <td><span :class="['telegram-status-badge', statusClass(record.status)]">{{ statusLabel(record.status) }}</span></td>
            <td>{{ record.created_by_name || "-" }}</td>
            <td>{{ record.sent_by_name || "-" }}</td>
            <td>{{ formatDateTime(record.created_at) }}</td>
            <td>{{ formatDateTime(record.scheduled_at) }}</td>
            <td class="text-center">
              <div class="telegram-action-group">
                <button
                  type="button"
                  class="telegram-table-action telegram-view-action"
                  v-tooltip="$t('telegramMessage.viewDetail')"
                  @click="viewDetail(record)"
                >
                  <i class="fas fa-eye" />
                </button>
                <button
                  v-if="canEdit(record)"
                  type="button"
                  class="telegram-table-action telegram-edit-action"
                  v-tooltip="$t('telegramMessage.editDraft')"
                  @click="editDraft(record)"
                >
                  <i class="fas fa-edit" />
                </button>
                <a-popconfirm
                  v-if="canDelete(record)"
                  placement="leftTop"
                  :title="$t('are_you_sure_to_delete')"
                  :ok-text="$t('yes')"
                  :cancel-text="$t('no')"
                  @confirm="deleteRecord(record)"
                >
                  <a
                    href="#"
                    :class="['telegram-table-action tw-no-underline', 'telegram-delete-action', { 'is-disabled': isDeleting(record) }]"
                    v-tooltip="isDeleting(record) ? $t('telegramMessage.deleting') : $t('delete')"
                    @click.prevent
                  >
                    <i v-if="isDeleting(record)" class="fas fa-circle-notch fa-spin" />
                    <i v-else class="fas fa-trash" />
                  </a>
                </a-popconfirm>
              </div>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end tw-p-4">
        <ts-pagination v-model="pagination" @navigate="fetchData" />
      </div>
    </ts-panel>

    <telegram-message-detail-modal
      :visible="showDetail"
      :record="selectedRecord"
      :loading="detailLoading"
      @page-change="loadDetailRecipients"
      @close="closeDetail"
    />
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import TelegramMessageDetailModal from './components/TelegramMessageDetailModal.vue'

export default {
  name: 'telegramMessageHistory',
  components: {
    TelegramMessageDetailModal
  },
  data: () => ({
    loading: false,
    detailLoading: false,
    showDetail: false,
    selectedRecord: null,
    deletingMessageId: null
  }),
  computed: {
    ...mapState('notification/telegramMessage', ['histories', 'pagination']),
    search: {
      get () { return this.$store.state.notification.telegramMessage.search },
      set (value) {
        this.$store.commit('notification/telegramMessage/SET_SEARCH', value)
        this.$store.commit('notification/telegramMessage/SET_PAGE', 1)
      }
    },
    dateRange: {
      get () { return this.$store.state.notification.telegramMessage.dateRange },
      set (value) { this.$store.commit('notification/telegramMessage/SET_DATE_RANGE', value) }
    },
    columns () {
      return [
        { name: this.$t('telegramMessage.title') },
        { name: this.$t('telegramMessage.content') },
        { name: this.$t('telegramMessage.audience') },
        { name: this.$t('telegramMessage.recipientCount') },
        { name: this.$t('telegramMessage.status') },
        { name: this.$t('telegramMessage.createdBy') },
        { name: this.$t('telegramMessage.sentBy') },
        { name: this.$t('telegramMessage.createdAt') },
        { name: this.$t('telegramMessage.scheduledAt') },
        { name: this.$t('actions'), style: 'text-align:center; width: 1px;' }
      ]
    }
  },
  created () { this.fetchData() },
  watch: {
    search: debounce(function () { this.fetchData() }, 500)
  },
  methods: {
    ...mapActions('notification/telegramMessage', ['fetchHistory', 'findDetail', 'deleteMessage']),
    statusClass (status) {
      return `telegram-status-${status}`
    },
    statusLabel (status) {
      return ({
        draft: this.$t('telegramMessage.draft'),
        scheduled: this.$t('telegramMessage.scheduled'),
        pending: this.$t('telegramMessage.sending'),
        completed: this.$t('telegramMessage.sent'),
        sent: this.$t('telegramMessage.sent'),
        failed: this.$t('telegramMessage.failed'),
        partially_failed: this.$t('telegramMessage.partiallyFailed')
      })[status] || status
    },
    truncateText (value, length) {
      if (!value) return '-'
      return value.length > length ? value.slice(0, length) + '...' : value
    },
    formatDateTime (value) {
      return value ? moment(value).format('DD-MM-YYYY HH:mm') : '-'
    },
    viewDetail (record) {
      this.showDetail = true
      this.selectedRecord = Object.assign({}, record, { recipients: [] })
      this.loadDetailRecipients(1)
    },
    loadDetailRecipients (page = 1) {
      if (!this.selectedRecord) return
      this.detailLoading = true
      this.findDetail({
        id: this.selectedRecord.id,
        params: { page, perPage: 100 }
      }).then(detail => {
        this.selectedRecord = detail
      }).catch(error => {
        this.$notify({ type: 'error', text: error.message })
        this.closeDetail()
      }).finally(() => {
        this.detailLoading = false
      })
    },
    closeDetail () {
      this.showDetail = false
      this.detailLoading = false
      this.selectedRecord = null
    },
    normalizedStatus (record) {
      return String(record.status || '').trim().toLowerCase()
    },
    canEdit (record) {
      return ['draft', 'scheduled', 'failed'].includes(this.normalizedStatus(record))
    },
    canDelete (record) {
      return ['draft', 'scheduled', 'failed'].includes(this.normalizedStatus(record))
    },
    editDraft (record) {
      this.$router.push({ name: 'telegram-message-edit', params: { id: record.id } })
    },
    isDeleting (record) {
      return this.deletingMessageId === record.id
    },
    deleteRecord (record) {
      if (this.isDeleting(record)) return

      this.deletingMessageId = record.id
      this.deleteMessage(record.id).then(() => {
        this.$notify({ type: 'success', text: this.$t('telegramMessage.messageDeleted') })
        return this.fetchData({ currentPage: this.pagination.currentPage })
      }).catch(error => {
        this.$notify({ type: 'error', text: error.message })
      }).finally(() => {
        this.deletingMessageId = null
      })
    },
    fetchData (attributes) {
      this.loading = true
      return this.fetchHistory({
        params: {
          page: attributes && attributes.currentPage ? attributes.currentPage : 1,
          perPage: 15
        }
      }).finally(() => { this.loading = false })
    }
  }
}
</script>

<style scoped>
.telegram-title-cell { max-width: 220px; }
.telegram-content-cell { max-width: 360px; }
.telegram-title-cell, .telegram-content-cell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.telegram-status-badge { border-radius: 999px; display: inline-block; font-size: 11px; font-weight: 600; line-height: 1.2; padding: 5px 10px; text-transform: capitalize; }
.telegram-status-draft { background: #f1f5f9; color: #475569; }
.telegram-status-scheduled { background: #ede9fe; color: #6d28d9; }
.telegram-status-pending { background: #fef3c7; color: #92400e; }
.telegram-status-completed, .telegram-status-sent { background: #dcfce7; color: #166534; }
.telegram-status-failed { background: #fee2e2; color: #b91c1c; }
.telegram-status-partially_failed { background: #ffedd5; color: #c2410c; }
.telegram-action-group { align-items: center; display: inline-flex; gap: 6px; justify-content: center; }
.telegram-table-action { align-items: center; background: transparent; border: 0; border-radius: 6px; color: #64748b; display: inline-flex; height: 28px; justify-content: center; transition: background-color .18s ease, color .18s ease, transform .18s ease; width: 30px; }
.telegram-view-action { color: #334155; }
.telegram-edit-action { color: #2563eb; }
.telegram-delete-action { color: #dc2626; }
.telegram-table-action:hover { transform: translateY(-1px); }
.telegram-view-action:hover { background: #f1f5f9; color: #334155; }
.telegram-edit-action:hover { background: #eff6ff; color: #2563eb; }
.telegram-delete-action:hover { background: #fff1f2; color: #dc2626; }
.telegram-delete-action.is-disabled { background: transparent; color: #94a3b8; cursor: wait; pointer-events: none; transform: none; }
.telegram-table-action:focus { box-shadow: 0 0 0 3px rgba(37,99,235,.16); outline: 0; }
</style>
